import { showError, showSuccess } from "common/ToastNotifications";
import convertDsltoQueryBuilder from "common/convertDslToQueryBuilder";
import getSearchFieldsErrorMessage from "common/getSearchFieldsErrorMessage";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import MenuPanelContainer from "components/atoms/MenuPanelContainer";
import SidePanel from "components/atoms/SidePanel";
import AttachmentTab from "components/molecules/AttachmentTab";
import DataObjectSearchForm from "components/molecules/DataObjectSearchForm";
import DataObjectSearchResultList from "components/molecules/DataObjectSearchResultList";
import DataSetRecordForm from "components/molecules/DataSetRecordForm";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import AppPaths from "constants/appPaths";
import React from "react";
import { RuleGroupType } from "react-querybuilder";
import { ConnectedProps, connect } from "react-redux";
import { useParams } from "react-router-dom";
import { Action, Dispatch } from "redux";
import AppState from "store/AppState";
import actions from "store/actions";
import NaturalLanguageQuery from "./NaturalLanguageQuery";

const mapStateToProps = (state: AppState) => {
    const { businessAreas: userBusinessAreas, activeBusinessAreaId, getUserBusinessAreasResult } = state.home;

    const { getSearchFieldsResult, getUserSearchDataObjectsPermisionResult, searchDataObjectsResult, searchResult, searchFields, userSearchDataObjectsPermision } =
        state.dataObjectSearch;

    const { getSchemaModelResult, getSchemaModelsResult, schemaModel, schemaModelListResult } = state.schemaModels;

    const { getSchemaDataSetResult, schemaDataSetResult } = state.dataSets;

    const { dataObject, getDataObjectResult, updateDataObjectResult } = state.dataObjects;

    const rawQuery = state.queryBuilderObject.query;

    return {
        // home
        activeBusinessAreaId,
        userBusinessAreas,
        getUserBusinessAreasResult,
        // dataObjectSearch
        getSearchFieldsResult,
        getUserSearchDataObjectsPermisionResult,
        searchDataObjectsResult,
        searchResult,
        searchFields,
        userSearchDataObjectsPermision,
        // dataSets
        getSchemaDataSetResult,
        schemaDataSetResult,
        // schemaModels
        getSchemaModelResult,
        getSchemaModelsResult,
        schemaModel,
        schemaModelListResult,
        // dataObjects
        dataObject,
        getDataObjectResult,
        updateDataObjectResult,
        // For Query Builder fields
        rawQuery,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action<string>>) => {
    const searchDataObjects: IDataObjectsSearchRequest = (query, searchId, queryType, businessArea, schema, dataSet, from, size, showPIIData) => {
        dispatch(actions.searchDataObjectsRequest(query, searchId, queryType, businessArea, schema, dataSet, from, size, showPIIData));
    };
    return {
        getUserSearchDataObjectsPermission: () => dispatch(actions.getUserSearchDataObjectsPermissionRequest()),
        searchDataObjects,
        getSchemaModels: (businessAreaId: string) => dispatch(actions.getSchemaModelsRequest(businessAreaId)),
        getDataSetBySchemaId: (businessAreaId: string, schemaId: string) => dispatch(actions.getDataSetBySchemaIdRequest(businessAreaId, schemaId)),
        getSchemaModel: (businessAreaId: string, schemaId: string) => dispatch(actions.getSchemaModelRequest(businessAreaId, schemaId)),
        getSearchFields: (businessArea: string, schema: string, dataSet: string) => dispatch(actions.getSearchFieldsRequest(businessArea, schema, dataSet)),
        getDataObject: (businessAreaId: string, schemaId: string, dataSetId: string, dataObjectId: string, showPIIData: boolean) =>
            dispatch(actions.getDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId, showPIIData)),
        clearSearchDataObjectsResult: () => dispatch(actions.clearSearchDataObjectsResultRequest()),
        setQueryBuilderObjectAction: (_q: RuleGroupType | string | null) => dispatch(actions.setQueryBuilderObjectAction(_q)),
        updateDataObject: (businessAreaId: string, schemaId: string, dataSetId: string, dataObjectId: string, dataObject: IDataObject | IBasicFormDataObject) =>
            dispatch(actions.updateDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId, dataObject)),
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type IPropsFromRedux = ConnectedProps<typeof connector>;

interface IDataObjectSearchProps extends IPropsFromRedux {
    setBreadcrumbNavigationItems?: (items: IBreadcrumbItem[]) => void;
}

function DataObjectSearch({
    activeBusinessAreaId,
    dataObject,
    getUserBusinessAreasResult,
    getDataObjectResult,
    getSchemaDataSetResult,
    getSearchFieldsResult,
    getSchemaModelResult,
    getSchemaModelsResult,
    getUserSearchDataObjectsPermisionResult,
    rawQuery,
    schemaDataSetResult,
    schemaModel,
    schemaModelListResult,
    searchDataObjectsResult,
    searchFields,
    searchResult,
    updateDataObjectResult,
    userBusinessAreas,
    userSearchDataObjectsPermision,
    clearSearchDataObjectsResult,
    getDataObject,
    getDataSetBySchemaId,
    getSchemaModel,
    getSchemaModels,
    getSearchFields,
    getUserSearchDataObjectsPermission,
    searchDataObjects,
    setBreadcrumbNavigationItems,
    setQueryBuilderObjectAction,
    updateDataObject,
}: IDataObjectSearchProps) {
    const [fetchingPermission, setFetchingPermission] = React.useState<boolean>(true);
    const [permissionDenied, setPermissionDenied] = React.useState<boolean>(false);
    const [loadingSchemaList, setLoadingSchemaList] = React.useState<boolean>(true);
    const [loadingDataSetList, setLoadingDataSetList] = React.useState<boolean>(true);
    const [showDataObjectFormContainer, setShowDataObjectFormContainer] = React.useState<boolean>(false);
    const [closeDataObject, setCloseDataObject] = React.useState<boolean>(false);
    const [fetchingSearchResult, setFetchingSearchResult] = React.useState<boolean>(true);
    const [dataObjectPermissionDenied, setDataObjectPermissionDenied] = React.useState<boolean>(false);
    const [fetchingDataObject, setFetchingDataObject] = React.useState<boolean>(true);
    const [updatingDataObject, setUpdatingDataObject] = React.useState<boolean>(false);
    const [schemaPermissionDenied, setSchemaPermissionDenied] = React.useState<boolean>(false);
    const [fetchingSchemaModel, setFetchingSchemaModel] = React.useState<boolean>(true);
    const [sidePanelActiveTab, setSidePanelActiveTab] = React.useState<string>("DATASET_RECORD");
    const [queryType, setQueryType] = React.useState<IQueryType>("QUERY");
    const [searchId, setSearchId] = React.useState<string>();
    const [naturallyDerivedDslQuery, setNaturallyDerivedDslQuery] = React.useState<IDslCompoundQueryClause | null>(null);

    // Check if still loading Query Builder properties
    const isLoadingBuilderProperties = React.useMemo(() => getSearchFieldsResult?.inProgress ?? true, [getSearchFieldsResult]);
    const builderPropertiesError = React.useMemo(() => getSearchFieldsErrorMessage(getSearchFieldsResult), [getSearchFieldsResult]);

    const [searchInput, setSearchInput] = React.useState<ISearchFormValueType>({
        query: null,
        queryType: "QUERY",
        businessArea: "all",
        schema: "all",
        dataSet: "all",
        showPIIData: false,
    });

    const { tenant } = useParams<{ tenant: string }>();

    const showBreadCrumbNavigationItems = React.useCallback(() => {
        if (setBreadcrumbNavigationItems) {
            const breadcrumbNavigationItems: IBreadcrumbItem[] = [
                {
                    route: AppPaths.TENANT_HOME.replace(":tenant", tenant.toLowerCase()),
                    text: "Home",
                },
                {
                    route: AppPaths.TENANT_DATA_OBJECT_SEARCH.replace(":tenant", tenant.toLowerCase()),
                    text: "Search",
                },
            ];
            setBreadcrumbNavigationItems(breadcrumbNavigationItems);
        }
    }, [setBreadcrumbNavigationItems, tenant]);

    React.useEffect(() => {
        // On mount
        getUserSearchDataObjectsPermission();
        showBreadCrumbNavigationItems();

        // On dismount
        return () => {
            clearSearchDataObjectsResult();
        };
    }, [clearSearchDataObjectsResult, getUserSearchDataObjectsPermission, showBreadCrumbNavigationItems]);

    React.useEffect(() => {
        if (getUserSearchDataObjectsPermisionResult && userSearchDataObjectsPermision) {
            setPermissionDenied(!getUserSearchDataObjectsPermisionResult.success || !userSearchDataObjectsPermision.canView);
            setFetchingPermission(false);
        } else {
            setFetchingPermission(true);
        }
    }, [getUserSearchDataObjectsPermisionResult, userSearchDataObjectsPermision]);

    React.useEffect(() => {
        if (getSchemaModelsResult) {
            setLoadingSchemaList(false);
        }
    }, [getSchemaModelsResult]);

    React.useEffect(() => {
        if (getSchemaDataSetResult) {
            setLoadingDataSetList(false);
        }
    }, [getSchemaDataSetResult]);

    React.useEffect(() => {
        if (searchDataObjectsResult) {
            if (!searchDataObjectsResult.success) {
                if (searchDataObjectsResult.code === "PERMISSION_DENIED") {
                    setPermissionDenied(true);
                } else {
                    showError("Unable to search dataset records.");
                }
            } else {
                if (searchInput.queryType === "NATURAL_LANGUAGE_QUERY") {
                    setShowDataObjectFormContainer(true);
                    setCloseDataObject(true);
                } else {
                    setShowDataObjectFormContainer(false);
                }
            }
            setFetchingSearchResult(searchDataObjectsResult.inProgress || false);
        }
    }, [searchDataObjectsResult, searchInput.queryType]);

    React.useEffect(() => {
        if (getDataObjectResult) {
            if (!getDataObjectResult.success) {
                if (getDataObjectResult.code === "PERMISSION_DENIED") {
                    setDataObjectPermissionDenied(true);
                } else if (getDataObjectResult.code === "NOT_FOUND_ERROR") {
                    showError("Record not found!");
                } else {
                    showError("Unable to load dataset record.");
                }
            }
            setFetchingDataObject(false);
        }
    }, [getDataObjectResult]);

    const saveUpdate = React.useCallback(
        (dataId: string, updatedObject: IDataObject | IBasicFormDataObject) => {
            if (!!dataObject) {
                setUpdatingDataObject(true);
                updateDataObject(dataObject.businessAreaId, dataObject.schemaId, dataObject.dataSetId, dataId, updatedObject);
            }
        },
        [dataObject, updateDataObject],
    );

    React.useEffect(() => {
        if (updateDataObjectResult) {
            if (!updateDataObjectResult.success) {
                if (updateDataObjectResult.code === "PERMISSION_DENIED") {
                    setDataObjectPermissionDenied(true);
                } else if (updateDataObjectResult.code === "NOT_FOUND_ERROR") {
                    showError("Record not found!");
                } else {
                    showError("Could not update dataset record.");
                }
            }

            if (updateDataObjectResult.success && updatingDataObject) {
                showSuccess("Dataset record updated successfully.");
                setCloseDataObject(true);
                if (searchInput.queryType !== "NATURAL_LANGUAGE_QUERY") {
                    setShowDataObjectFormContainer(false);
                }
            }
        }
        setUpdatingDataObject(false);
    }, [getDataObject, searchInput.queryType, updateDataObjectResult, updatingDataObject]);

    React.useEffect(() => {
        if (getSchemaModelResult) {
            if (!getSchemaModelResult.success) {
                if (getSchemaModelResult.code === "PERMISSION_DENIED") {
                    setSchemaPermissionDenied(true);
                } else {
                    showError("Unable to get schema.");
                }
            }
            setFetchingSchemaModel(false);
        }
    }, [getSchemaModelResult]);

    const handleSearch = React.useCallback(
        (_searchInput: ISearchFormValueType) => {
            if (!_searchInput.queryType) {
                return; // Prevent wrong input
            }

            if (_searchInput.query && _searchInput.query.trim()) {
                searchDataObjects(
                    _searchInput.query,
                    null,
                    _searchInput.queryType,
                    _searchInput.businessArea,
                    _searchInput.schema,
                    _searchInput.dataSet,
                    0,
                    100,
                    _searchInput.showPIIData,
                );
                setFetchingSearchResult(true);
                setShowDataObjectFormContainer(false);
                setQueryType(_searchInput.queryType);
            }
            setSearchInput(_searchInput);
        },
        [searchDataObjects],
    );

    const searchBySearchId = React.useCallback(
        (_searchId: string) => {
            if (searchInput.query && searchInput.query.trim()) {
                searchDataObjects(null, _searchId, searchInput.queryType, searchInput.businessArea, searchInput.schema, searchInput.dataSet, 0, 100, searchInput.showPIIData);
                setFetchingSearchResult(true);
                setShowDataObjectFormContainer(false);
            }
            setSearchId(_searchId);
        },
        [searchDataObjects, searchInput.businessArea, searchInput.dataSet, searchInput.query, searchInput.queryType, searchInput.schema, searchInput.showPIIData],
    );

    const onSearchPageSelect = React.useCallback(
        (pagination: IPagination) => {
            if (searchInput.query && searchInput.query.trim()) {
                const _searchId = searchId ?? (searchResult && searchResult.searchId) ?? null;
                searchDataObjects(
                    searchInput.query,
                    _searchId,
                    searchInput.queryType,
                    searchInput.businessArea,
                    searchInput.schema,
                    searchInput.dataSet,
                    (pagination.current - 1) * pagination.pageSize,
                    pagination.pageSize,
                    searchInput.showPIIData,
                );
                setFetchingSearchResult(true);

                if (searchInput.queryType === "NATURAL_LANGUAGE_QUERY") {
                    setShowDataObjectFormContainer(true);
                    setCloseDataObject(true);
                } else {
                    setShowDataObjectFormContainer(false);
                }
            }
        },
        [
            searchInput.query,
            searchInput.queryType,
            searchInput.businessArea,
            searchInput.schema,
            searchInput.dataSet,
            searchId,
            searchInput.showPIIData,
            searchResult,
            searchDataObjects,
        ],
    );

    const onBusinessAreaChange = React.useCallback(
        (businessAreaId: string) => {
            setLoadingSchemaList(true);
            getSchemaModels(businessAreaId);
        },
        [getSchemaModels],
    );

    const onSchemaChange = React.useCallback(
        (businessAreaId: string, schemaId: string) => {
            setLoadingDataSetList(true);
            getDataSetBySchemaId(businessAreaId, schemaId);
        },
        [getDataSetBySchemaId],
    );

    const [selectedRowKeys, setSelectedRowKeys] = React.useState<string[]>([]);

    const onDataObjectSelected = React.useCallback(
        (dataRecord: IDataObjectRecord) => {
            if (!dataRecord) {
                return;
            }

            setShowDataObjectFormContainer(true);
            setCloseDataObject(false);

            setSchemaPermissionDenied(false);
            setDataObjectPermissionDenied(false);
            setFetchingSchemaModel(true);
            setFetchingDataObject(true);
            setSelectedRowKeys([dataRecord._dataObjectId]);

            getSchemaModel(dataRecord._businessAreaId, dataRecord._schemaId);
            getDataObject(dataRecord._businessAreaId, dataRecord._schemaId, dataRecord._dataSetId, dataRecord._dataObjectId, dataRecord._showPIIData);
        },
        [getDataObject, getSchemaModel],
    );

    const onQueryBuilderSearch = React.useCallback(
        (dslQuery: Record<string, any> | null | undefined, _businessAreaId: string, _schemaId: string, _dataSetId: string, showPIIData: boolean) => {
            if (dslQuery) {
                searchDataObjects(JSON.stringify(dslQuery), null, "DSL_QUERY", _businessAreaId, _schemaId, _dataSetId, 0, 100, showPIIData);
                // Set states
                setFetchingSearchResult(true);
                setShowDataObjectFormContainer(false);
                setQueryType("DSL_QUERY");
                setSearchInput({
                    query: JSON.stringify(dslQuery),
                    queryType: "DSL_QUERY",
                    businessArea: _businessAreaId,
                    schema: _schemaId,
                    dataSet: _dataSetId,
                    showPIIData: showPIIData,
                });
            }
        },
        [searchDataObjects],
    );

    const handleUpdateQuery = React.useCallback(
        (_query: RuleGroupType | string | null) => {
            setQueryBuilderObjectAction(_query);
        },
        [setQueryBuilderObjectAction],
    );

    const handleQueryTypeChange = React.useCallback((q: IQueryType) => {
        setQueryType(q);
        setShowDataObjectFormContainer(false); // Change of tab, hide the dataset form
    }, []);

    const handleEditNaturalLanagugeInBuilder = React.useCallback(
        (dsl: IDslCompoundQueryClause) => {
            handleQueryTypeChange("DSL_QUERY"); // Switch to Query Builder
            setNaturallyDerivedDslQuery(dsl);
        },
        [handleQueryTypeChange],
    );

    React.useEffect(() => {
        if (!isLoadingBuilderProperties && naturallyDerivedDslQuery && searchFields.length > 0) {
            const builderQuery = convertDsltoQueryBuilder(naturallyDerivedDslQuery, searchFields);
            handleUpdateQuery(builderQuery);
        }
    }, [handleUpdateQuery, naturallyDerivedDslQuery, searchFields, isLoadingBuilderProperties]);

    return !fetchingPermission && permissionDenied ? (
        <ProhibitedArea />
    ) : (
        <div className="relative flex size-full flex-1 gap-4 overflow-auto">
            <div className="relative flex flex-1 flex-col overflow-auto">
                <LoadingOverlay busy={fetchingPermission} message="Please wait..." />
                <FlexContainerLayout
                    showHeader={true}
                    header={
                        <DataObjectSearchForm
                            selectedQueryType={queryType}
                            onQueryTypeChange={handleQueryTypeChange}
                            onSearch={handleSearch}
                            activeBusinessAreaId={activeBusinessAreaId}
                            isLoadingBusinessAreas={getUserBusinessAreasResult ? false : true}
                            businessAreas={userBusinessAreas}
                            onBusinessAreaChange={onBusinessAreaChange}
                            loadingSchemaList={loadingSchemaList}
                            schemaList={schemaModelListResult}
                            onSchemaChange={onSchemaChange}
                            loadingDataSetList={loadingDataSetList}
                            dataSetList={schemaDataSetResult}
                            // For Query Builder
                            rawQuery={rawQuery}
                            builderProperties={searchFields}
                            isLoadingBuilderProperties={isLoadingBuilderProperties}
                            builderPropertiesError={builderPropertiesError}
                            onUpdateQuery={handleUpdateQuery}
                            onSearchBuilderProperties={getSearchFields}
                            onQueryBuilderSearch={onQueryBuilderSearch}
                        />
                    }
                >
                    <DataObjectSearchResultList
                        hits={fetchingSearchResult ? [] : searchResult.hits}
                        loading={fetchingSearchResult}
                        onDataObjectSelected={onDataObjectSelected}
                        totalHits={searchResult.total}
                        selectedRowKeys={selectedRowKeys}
                        onPageSelect={onSearchPageSelect}
                    />
                </FlexContainerLayout>
            </div>
            {showDataObjectFormContainer && (
                <SidePanel>
                    {queryType === "NATURAL_LANGUAGE_QUERY" && (
                        <div className="flex size-full flex-1 flex-col overflow-auto">
                            <p className="border-b border-b-slate-200 px-4 py-2 text-xl font-bold">Question Query</p>
                            {searchResult?.error ? (
                                <div className="flex flex-1 flex-col gap-2 overflow-auto rounded-sm bg-red-100 px-4 py-2">
                                    <p className="text-lg font-bold text-red-800">ERROR:</p>
                                    <p>{(searchResult.error as unknown as IHttpMessage).error?.message ?? "An unknown error has occured."}</p>
                                </div>
                            ) : (
                                <NaturalLanguageQuery
                                    naturalLanguageText={searchInput?.query}
                                    dslQuery={searchResult?.dslQuery}
                                    error={searchResult?.error}
                                    searchId={searchResult?.searchId}
                                    properties={searchResult?.properties}
                                    onSearch={(_searchId: string) => searchBySearchId(_searchId)}
                                    onEditInBuilder={handleEditNaturalLanagugeInBuilder}
                                />
                            )}
                        </div>
                    )}
                    {!closeDataObject && queryType !== "NATURAL_LANGUAGE_QUERY" && (
                        <MenuPanelContainer
                            recordKeys={{
                                DATASET_RECORD: "Dataset Record",
                                ATTACHMENTS: "Attachments",
                            }}
                            selectedKey={sidePanelActiveTab}
                            onChange={setSidePanelActiveTab}
                        >
                            {sidePanelActiveTab === "DATASET_RECORD" && (
                                <DataSetRecordForm
                                    isLoading={fetchingSchemaModel || fetchingDataObject || updatingDataObject}
                                    loadingMessage={updatingDataObject ? "Updating dataset record..." : "Getting dataset record..."}
                                    isProhibited={dataObjectPermissionDenied || schemaPermissionDenied}
                                    dataObject={dataObject}
                                    schemaModel={schemaModel}
                                    onCancel={() => {
                                        setCloseDataObject(true);
                                        setShowDataObjectFormContainer(false);
                                        setSelectedRowKeys([]);
                                    }}
                                    onSave={(dataId, dataObj) => saveUpdate(dataId, dataObj)}
                                />
                            )}
                            {sidePanelActiveTab === "ATTACHMENTS" &&
                                (dataObject ? (
                                    <AttachmentTab
                                        dataObject={dataObject}
                                        schemaFields={(schemaModel?.fields || []) as any}
                                        onDownload={(file: any) => console.log("Download attachment:", file)}
                                    />
                                ) : (
                                    <div className="flex h-64 items-center justify-center">
                                        <p className="text-gray-500">No record selected</p>
                                    </div>
                                ))}
                        </MenuPanelContainer>
                    )}
                </SidePanel>
            )}
        </div>
    );
}

export default connector(DataObjectSearch);
